import {
  replaceOrAppendItem,
  useAllItems,
  useConversation,
  type ColorValue,
} from '@/services/ConversationService';
import './TilePreview.css';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  ActionButton,
  ColorIndicator,
  Column,
  HoverElement,
  HoverElementBoundary,
  IconButton,
  PaletteInput,
  Row,
  ThinBorderStyle,
  TypeaheadInput,
} from './DesignSystem';
import { NewVersionID, useVersions } from './VersionsPreview';
import { Spacer, useColorPreview } from './ColorPreview';
import { TileHeader } from './TileHeader';

type TypographyPreviewReducerState = {
  previewText?: string;
  isInSlate: boolean;
};

export type TypographyID = string;
export function NewTypographyID(): TypographyID {
  return crypto.randomUUID();
}

export const FontOptions = [
  'Arial',
  'Courier New',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
];

// TODO(LAB-260): Explore how we will handle identity and `TypographyStyle`s
export type TypographyStyle = {
  id: TypographyID;
  name: string;
  details: TextDetails;
};

export type TextStyle =
  | {
      type: 'details';
      details: TextDetails;
    }
  | {
      type: 'style';
      style: string;
    };

export type TextDetails = {
  fontSize: number;
  fontName: string;
  textColor?: ColorValue;
};

type TypographyPreviewComputedState = {
  typographyStyles: TypographyStyle[];
};

type TypographyPreviewReducerAction =
  | {
      type: 'previewText';
      text: string;
    }
  | { type: 'clearPreview' }
  | { type: 'setIsInSlate'; isInSlate: boolean };
type TypographyPreviewReducerDispatch = (
  action: TypographyPreviewReducerAction,
) => void;

export type TypographyPreviewDispatchHelpers = {
  addStyle: ({ typographyStyle }: { typographyStyle: TypographyStyle }) => void;

  changeStyle: ({
    priorName,
    typographyStyle,
  }: {
    priorName: string;
    typographyStyle: TypographyStyle;
  }) => void;

  removeStyle: ({ name }: { name: string }) => void;
};

type TypographyPreviewContextProps = {
  state: TypographyPreviewReducerState;
  dispatch: TypographyPreviewReducerDispatch;
  dispatchHelpers: TypographyPreviewDispatchHelpers;
};
type TypographyPreviewExternalContextProps = {
  state: TypographyPreviewReducerState & TypographyPreviewComputedState;
  dispatch: TypographyPreviewReducerDispatch;
  dispatchHelpers: TypographyPreviewDispatchHelpers;
};
const TypographyPreviewContext =
  createContext<TypographyPreviewContextProps | null>(null);

export function useTextStyle({
  textStyle,
}: {
  textStyle: TextStyle | null;
}): TextDetails | undefined {
  const { state } = useTypographyPreview();
  if (textStyle === null) {
    return undefined;
  }

  switch (textStyle.type) {
    case 'details':
      return textStyle.details;
    case 'style':
      return state.typographyStyles.find(
        (style) => style.name === textStyle.style,
      )?.details;
  }
}

export function useTypographyPreview(): TypographyPreviewExternalContextProps {
  const context = useContext(TypographyPreviewContext);
  if (context === null) {
    throw new Error(
      'useTypographyPreview must be used within a TypographyPreviewBoundary',
    );
  }
  const { state, dispatch, dispatchHelpers } = context;
  const {
    state: { activeVersionID },
  } = useVersions();

  const allItems = useAllItems({ activeVersionID });
  function getTypographyStylesFromConversation(
    items: ConversationItem[],
  ): TypographyStyle[] {
    return items.reduce<TypographyStyle[]>((acc, e) => {
      const outTypographyStylesWithName =
        (name: string) => (candidate: TypographyStyle) => {
          return candidate.name !== name;
        };

      switch (e.type) {
        case 'updateTextStyles': {
          const withoutRemovedStyles = e.removals.reduce(
            (accumulator, removal) => {
              const nameToRemove =
                typeof removal === 'string' ? removal : removal.name;
              return accumulator.filter(
                outTypographyStylesWithName(nameToRemove),
              );
            },
            acc,
          );

          return e.additions.reduce((accumulator, addition) => {
            return replaceOrAppendItem({
              items: accumulator,
              newItem: {
                id: addition.id,
                name: addition.name,
                details: addition.style,
              },
              predicate: (style) => style.id === addition.id,
            });
          }, withoutRemovedStyles);
        }
        case 'defineTextStyle': {
          return replaceOrAppendItem({
            items: acc,
            newItem: { id: e.id, name: e.name, details: e.style },
            predicate: (style) => style.id === e.id,
          });
        }
        case 'removeTextStyle': {
          return acc.filter(outTypographyStylesWithName(e.name));
        }
        case 'defineColor':
        case 'removeColor':
        case 'updateColorPalette':
        case 'message':
        case 'addScreen':
        case 'removeScreen':
        case 'describeScreen':
        case 'connect':
        case 'writeProperties':
        case 'defineComponentStyle':
        case 'removeComponentStyle':
        case 'declareFunctionComponent':
        case 'removeFunctionComponent':
        case 'createComponent':
        case 'addChildComponent':
        case 'createVersion':
          return acc;
      }
    }, []);
  }

  const typographyStyles = useMemo(() => {
    return getTypographyStylesFromConversation(allItems)?.sort((a, b) =>
      a.id.localeCompare(b.id),
    );
  }, [allItems]);
  return { state: { ...state, typographyStyles }, dispatch, dispatchHelpers };
}

export function TypographyPreviewBoundary({
  children,
}: React.PropsWithChildren) {
  const { dispatch: conversationDispatch } = useConversation();
  function reducer(
    state: TypographyPreviewReducerState,
    action: TypographyPreviewReducerAction,
  ): TypographyPreviewReducerState {
    switch (action.type) {
      case 'previewText': {
        return {
          ...state,
          previewText: action.text,
        };
      }
      case 'clearPreview': {
        return {
          ...state,
          previewText: undefined,
        };
      }
      case 'setIsInSlate': {
        return {
          ...state,
          isInSlate: action.isInSlate,
        };
      }
    }
  }
  const [state, dispatch] = useReducer(reducer, {
    isInSlate: false,
  });

  const dispatchHelpers: TypographyPreviewDispatchHelpers = {
    addStyle: function ({ typographyStyle }) {
      conversationDispatch({
        type: 'add-typography-style',
        typographyStyle,
      });
    },
    removeStyle({ name }) {
      conversationDispatch({ type: 'remove-typography-style', name });
    },
    changeStyle({ priorName, typographyStyle }) {
      conversationDispatch({
        type: 'change-typography-style',
        priorName,
        typographyStyle,
      });
    },
  };

  return (
    <TypographyPreviewContext.Provider
      value={{ state, dispatch, dispatchHelpers }}
    >
      {children}
    </TypographyPreviewContext.Provider>
  );
}

export function TypographyPreview({
  typographyStyle,
}: {
  typographyStyle: TypographyStyle;
}) {
  const { state, dispatchHelpers } = useTypographyPreview();
  const { state: colorsState } = useColorPreview();
  const colorNames = useMemo(
    () => colorsState.colors.map((c) => c.name),
    [colorsState],
  );

  function FontPreview({ children }: React.PropsWithChildren) {
    const { resolveColor } = useColorPreview();

    const textColor = resolveColor(typographyStyle.details.textColor ?? null);

    return (
      <div
        style={{
          fontSize: typographyStyle.details.fontSize,
          fontFamily: typographyStyle.details.fontName,
          marginBlockStart: 8,
          color: textColor ?? 'auto',
        }}
      >
        {children}
      </div>
    );
  }

  function TextColorOption({ option }: { option: string }) {
    const { resolveColor } = useColorPreview();

    const textColor = resolveColor({ type: 'reference', reference: option });
    return (
      <Row style={{ width: '100%', alignItems: 'center' }}>
        <div
          style={{
            width: 12,
            height: 12,
            backgroundColor: textColor,
            marginInlineEnd: 8,
            borderRadius: 4,
            border: '1px solid black',
          }}
        />
        <div>{option}</div>
      </Row>
    );
  }

  function FontOption({ option }: { option: string }) {
    return <div style={{ fontFamily: option }}>{option}</div>;
  }

  const textColorName = useMemo(() => {
    if (typographyStyle.details.textColor === undefined) {
      return '';
    }

    switch (typographyStyle.details.textColor.type) {
      case 'reference':
        return typographyStyle.details.textColor.reference;
      case 'value':
        return typographyStyle.details.textColor.value.colorHex;
    }
  }, [typographyStyle.details.textColor]);
  const [draftFontName, setDraftFontName] = useState(
    typographyStyle.details.fontName,
  );
  const [draftFontSize, setDraftFontSize] = useState(
    `${typographyStyle.details.fontSize}`,
  );
  const [draftTextColorName, setDraftTextColorName] = useState(textColorName);

  useEffect(() => {
    setDraftFontName(typographyStyle.details.fontName);
    setDraftFontSize(`${typographyStyle.details.fontSize}`);
    setDraftTextColorName(textColorName);
  }, [
    textColorName,
    typographyStyle.details.fontName,
    typographyStyle.details.fontSize,
    typographyStyle.id,
  ]);

  function commitStyleDraft({
    fontName = draftFontName,
    fontSize = draftFontSize,
    textColor = draftTextColorName,
  }: {
    fontName?: string;
    fontSize?: string;
    textColor?: string;
  } = {}) {
    const parsedFontSize = Number(fontSize);
    const nextFontSize = Number.isNaN(parsedFontSize)
      ? typographyStyle.details.fontSize
      : parsedFontSize;
    const nextTextColor =
      textColor.length === 0
        ? undefined
        : { type: 'reference' as const, reference: textColor };
    const hasChanged =
      fontName !== typographyStyle.details.fontName ||
      nextFontSize !== typographyStyle.details.fontSize ||
      textColor !== textColorName;

    if (!hasChanged) {
      return;
    }

    dispatchHelpers.changeStyle({
      priorName: typographyStyle.name,
      typographyStyle: {
        ...typographyStyle,
        details: {
          ...typographyStyle.details,
          fontName,
          fontSize: nextFontSize,
          textColor: nextTextColor,
        },
      },
    });
  }

  const showPreviewText =
    state.previewText && state.previewText.length > 0 && !state.isInSlate;
  const rowStyle: React.CSSProperties = { marginInline: 8, marginBlockEnd: 8 };

  // The `shape` of the palette is the set of entity names in the palette.
  // During OOBE we don't want people to remove or rename colors (TBD),
  // so we "lock" the shape. This means they can't edit names, or remove colors.
  // TODO: Consider what editing we want to expose during OOBE, and if/when we want to "unlock" the shape
  const shapeOfPaletteIsLocked = true;

  return (
    <Row className="tile-preview" style={{ overflow: 'hidden' }}>
      <HoverElementBoundary>
        <Column style={{ width: '100%' }}>
          <TileHeader>
            <PaletteInput
              style={{
                background: 'none',
                border: 'none',
                paddingBlock: undefined,
              }}
              type="text"
              value={typographyStyle.name}
              placeholder="Style Name"
              fillWidth={true}
              disabled={shapeOfPaletteIsLocked}
              onChange={(name) => {
                dispatchHelpers.changeStyle({
                  priorName: typographyStyle.name,
                  typographyStyle: { ...typographyStyle, name },
                });
              }}
            />

            <Spacer />
            {shapeOfPaletteIsLocked || (
              <ActionButton
                action="remove"
                hidden={state.isInSlate}
                onClick={() => {
                  dispatchHelpers.removeStyle({ name: typographyStyle.name });
                }}
              />
            )}
          </TileHeader>

          <TypeaheadInput
            style={{
              ...rowStyle,
              marginBlockStart: rowStyle.marginBlockEnd,
            }}
            value={draftFontName}
            placeholder="Font Name"
            options={FontOptions}
            renderOption={FontOption}
            onChange={(fontName) => {
              setDraftFontName(fontName);
            }}
            onCommit={(fontName) => {
              commitStyleDraft({ fontName });
            }}
          />
          <Row>
            <PaletteInput
              type="number"
              value={draftFontSize}
              style={{
                ...rowStyle,
                width: '44px',
                alignSelf: 'flex-start',
              }}
              placeholder="Font Size"
              onChange={(fontSize) => {
                setDraftFontSize(fontSize);
              }}
              onBlur={() => {
                commitStyleDraft({ fontSize: draftFontSize });
              }}
            />
            <TypeaheadInput
              style={{
                ...rowStyle,
              }}
              value={draftTextColorName}
              onChange={(colorName) => {
                setDraftTextColorName(colorName);
              }}
              onCommit={(colorName) => {
                commitStyleDraft({ textColor: colorName });
              }}
              placeholder="Choose a Text Color"
              options={colorNames}
              renderOption={TextColorOption}
              renderOptionIndicator={ColorIndicator}
            />
          </Row>

          {showPreviewText && <FontPreview>{state.previewText}</FontPreview>}
        </Column>
      </HoverElementBoundary>
    </Row>
  );
}

export const EnableTypographyPalette = true;
export function MakeInitialTypographyConversationItems(): ConversationItem[] {
  if (!EnableTypographyPalette) {
    return [];
  }

  return [
    {
      type: 'defineTextStyle',
      id: NewTypographyID(),
      name: 'header',
      style: { fontSize: 24, fontName: 'Helvetica' },
    },
    {
      type: 'defineTextStyle',
      id: NewTypographyID(),
      name: 'body',
      style: { fontSize: 16, fontName: 'Helvetica' },
    },
    {
      type: 'defineTextStyle',
      id: NewTypographyID(),
      name: 'caption',
      style: { fontSize: 12, fontName: 'Helvetica' },
    },
    {
      type: 'createVersion',
      name: 'A type-tastic origin story!',
      id: NewVersionID(),
    },
  ];
}
